"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Link from "next/link";

export default function AboutTeaser() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 px-6 md:px-10" style={{ background: 'var(--cream-dark)' }} ref={ref}>
      <div className="max-w-8xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          {/* Image Column */}
          <div className="lg:col-span-5 reveal-hidden">
            <div className="relative">
              {/* Main portrait */}
              <div className="rounded-5xl overflow-hidden aspect-[4/5]" style={{ boxShadow: '0 20px 80px rgba(26,61,26,0.2)' }}>
                <AppImage
                  src="/assets/images/Claudiaa-1772742032185.png"
                  alt="Claudia Deichel, fundadora e instrutora do Studio Pilates Claudia Deichel em Porto Alegre"
                  fill
                  className="object-cover object-top"
                  priority
                />
              </div>
              {/* Decorative rings */}
              <div className="absolute -bottom-8 -right-8 w-56 h-56 rounded-full border" style={{ borderColor: 'rgba(122,158,126,0.25)' }} />
              <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full border" style={{ borderColor: 'rgba(201,168,76,0.2)' }} />
              {/* Experience badge */}
              <div className="absolute bottom-10 -right-4 md:right-4 glass-card rounded-3xl p-5 float-card">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--stone)' }}>Experiência</p>
                <p className="font-serif text-2xl font-medium" style={{ color: 'var(--forest)' }}>15+ Anos</p>
                <p className="text-xs mt-1" style={{ color: 'var(--stone)' }}>formando profissionais</p>
              </div>
            </div>
          </div>

          {/* Text Column */}
          <div className="lg:col-span-7">
            <span className="reveal-hidden subheading mb-5 block">Sobre o Studio</span>
            <h2 className="reveal-hidden reveal-delay-1 font-serif text-forest leading-tight mb-10"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)' }}>
              Referência em saúde e{" "}
              <span className="italic font-light text-sage">bem-estar</span>{" "}
              em Porto Alegre
            </h2>

            <div className="reveal-hidden reveal-delay-2 mb-12" style={{ borderLeft: '2px solid var(--gold)', paddingLeft: '2rem' }}>
              <p className="leading-relaxed mb-5" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                Referência em saúde e bem-estar na Zona Norte de Porto Alegre, o Studio Pilates Claudia Deichel oferece um espaço moderno, acolhedor e equipado para proporcionar qualidade de vida, prevenção de dores e fortalecimento do corpo.
              </p>
              <p className="leading-relaxed" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                Nossa equipe é altamente qualificada e preparada para oferecer atendimento personalizado, respeitando as necessidades de cada aluno.
              </p>
            </div>

            {/* Stats row */}
            <div className="reveal-hidden reveal-delay-3 grid grid-cols-3 gap-5 mb-12">
              {[
                { value: "15+", label: "Anos de experiência" },
                { value: "3", label: "Unidades em POA" },
                { value: "1.2k+", label: "Alunos ativos" },
              ]?.map((stat) => (
                <div key={stat?.label} className="text-center py-6 px-4 rounded-3xl bg-white" style={{ boxShadow: '0 2px 16px rgba(26,61,26,0.06)' }}>
                  <p className="font-serif font-medium mb-1" style={{ fontSize: '2rem', color: 'var(--forest)' }}>{stat?.value}</p>
                  <p className="text-xs leading-snug" style={{ color: 'var(--stone)' }}>{stat?.label}</p>
                </div>
              ))}
            </div>

            <div className="reveal-hidden reveal-delay-4">
              <Link href="/about" className="btn-primary">
                Conheça nossa história
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}